import { ReactNode } from 'react';

import { SUPPORT_EMAIL } from 'support/helpers/const/const';
import { RelationshipV2 } from 'support/types';
import { ConnectionSetupTable } from 'components/ConnectionSetupTable/ConnectionSetupTable';
import { Section } from 'components/Display/Section/Section';
import { StepItem, Steps } from 'components/Display/Steps/Steps';
import { Translate } from 'components/Translate/Translate';

import { StepStage } from '../../../hooks/useSetupConnectionSteps/helpers';
import { ConnectionSetupStep } from '../../../hooks/useSetupConnectionSteps/useSetupConnectionSteps';
import { RelationshipTimeline } from './components/sections/RelationshipTimeline/RelationshipTimeline';

const STEP_METADATA: Record<StepStage, { title: ReactNode; description: ReactNode }> = {
  configuration: {
    title: <Translate i18nKey="connections:relationshipDetails.steps.configuration.title" />,
    description: <Translate i18nKey="connections:relationshipDetails.steps.configuration.description" />,
  },
  tasks: {
    title: <Translate i18nKey="connections:relationshipDetails.steps.tasks.title" />,
    description: <Translate i18nKey="connections:relationshipDetails.steps.tasks.description" />,
  },
  testing: {
    title: <Translate i18nKey="connections:relationshipDetails.steps.testing.title" />,
    description: <Translate i18nKey="connections:relationshipDetails.steps.testing.description" />,
  },
  goLive: {
    title: <Translate i18nKey="connections:relationshipDetails.steps.goLive.title" />,
    description: <Translate i18nKey="connections:relationshipDetails.steps.goLive.description" />,
  },
  done: { title: null, description: null },
  terminated: { title: null, description: null },
};

export const ConnectionSetupTab = ({
  relationship,
  steps,
}: {
  relationship: RelationshipV2;
  steps: Array<ConnectionSetupStep>;
}) => {
  const stepItems: Array<StepItem> = steps.map((step) => ({
    ...STEP_METADATA[step.stage],
    status: step.status,
    children: buildStepChildren(step),
  }));

  return (
    <Section.Vertical>
      <Steps steps={stepItems} />
      <RelationshipTimeline relationship={relationship} />
    </Section.Vertical>
  );
};

const TasksSupportText = () => (
  <p className="pt-4 text-xs leading-4 text-gray-500">
    <Translate
      i18nKey="connections:relationshipDetails.setupTable.support"
      components={{ link: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-900 underline" /> }}
    />
  </p>
);

const buildStepChildren = (step: ConnectionSetupStep): ReactNode => {
  if (step.stage === 'tasks') {
    return (
      <>
        <ConnectionSetupTable rows={step.rows} />
        <TasksSupportText />
      </>
    );
  }

  return <ConnectionSetupTable rows={step.rows} />;
};
